import {ApolloClient, ApolloLink, NextLink, NormalizedCacheObject, Operation} from '@apollo/client';
import {mergeTypeDefs} from '@graphql-tools/merge';

import createBatchLinkForwarder from 'graphql-client/createBatchLinkForwarder';
import createCsrfLink from 'graphql-client/createCsrfLink';
import createInMemoryCache from 'graphql-client/createInMemoryCache';
import createObservabilityLink from 'graphql-client/createObservabilityLink';
import createLazySchemaLink from 'graphql-client/schema-link/createLazySchemaLink';
import {createShareAccessApolloLink} from 'ShareAccess';
import lazyInit from 'util/lazyInit';

import createErrorLink from './createErrorLink';
import getOperationSignature from './getOperationSignature';
import GraphQLAllNodesSchemaDef from './schema/GraphQLAllNodesSchemaDef';
import GraphQLClientSchemaDef from './schema/GraphQLClientSchemaDef';
import {SyncManager} from './sync/SyncManager';

// This custom persisted queries link is pretty simple, so it's not a huge deal to maintain; the main
// annoyance will be keeping the logic in sync with apollo cli. As of writing, the logic is located at:
//   - https://github.com/apollographql/apollo-tooling/blob/4d32cdbcd717391e8557a053f48c90f7d204557c/packages/apollo/src/commands/client/extract.ts
//   - https://github.com/apollographql/apollo-tooling/blob/4d32cdbcd717391e8557a053f48c90f7d204557c/packages/apollo/src/utils/getOperationManifestFromProject.ts
//
// The most appealing alternative is https://github.com/apollographql/apollo-link-persisted-queries, but that
// is intended for the "Automatic Persisted Queries" flow (a negotiation protocol in which the server
// remembers queries sent by clients), but that differs significantly from our needs (extracting a whitelist
// at build time). A critical difference is that when the server returns an error, the link won't send hashes
// until page refresh (hashes are treated as optional rather than required).
const persistedQueriesLink = new ApolloLink((operation: Operation, forward: NextLink | undefined) => {
  const queryHash = getOperationSignature(operation.query, operation.operationName);
  operation.extensions.persistedQuery = {queryHash};
  return forward!(operation);
});

export type BenchlingApolloClient = ApolloClient<NormalizedCacheObject>;

export const getSyncManager = lazyInit<SyncManager>(() => new SyncManager());

export const getApolloClient = lazyInit<BenchlingApolloClient>(
  () =>
    new ApolloClient({
      cache: createInMemoryCache(),
      link: ApolloLink.from([
        createShareAccessApolloLink(),
        createErrorLink(),
        persistedQueriesLink,
        createCsrfLink(),
        getSyncManager().fragmentWatcherLink,
        createObservabilityLink(),
        global.__NODE_TESTING__ ? createLazySchemaLink() : createBatchLinkForwarder(),
      ]),
      connectToDevTools: Boolean(window.debug || window.DEVTOOLS_ENABLE_APOLLO_DEVTOOLS),
      typeDefs: mergeTypeDefs([GraphQLClientSchemaDef, GraphQLAllNodesSchemaDef]),
      assumeImmutableResults: true,
    })
);
